import axios from "axios";
import type { ScheduleOut } from "@/pages/human-resources/time-management/endpoints/schedules/inout/ScheduleOut.ts";
import type { ScheduleIn } from "@/pages/human-resources/time-management/endpoints/schedules/inout/ScheduleIn.ts";
import type { ValidateSchedulesIn } from "@/pages/human-resources/time-management/endpoints/schedules/inout/ValidateSchedulesIn.ts";
import type { CancelDraftSchedulesIn } from "@/pages/human-resources/time-management/endpoints/schedules/inout/CancelDraftSchedulesIn.ts";
import type { DeleteSchedulesIn } from "@/pages/human-resources/time-management/endpoints/schedules/inout/DeleteSchedulesIn.ts";
import { partition } from "@/shared/utils/Arrays.ts";

export async function writeSchedules(schedules: ScheduleIn[]): Promise<void> {
  const [toCreate, toUpdate] = partition(schedules, (s) => s.id === undefined);
  // Calling both in parallel might create error on backend due to compteurs
  // update, so we perform both calls in sequence:
  await createSchedules(toCreate);
  await updateSchedules(toUpdate);
}

export async function readEmployeesSchedules(
  siteId: string,
  relatedSiteIds: number[],
  employeesIds: number[],
  startDate: Date,
  endDate: Date,
): Promise<ScheduleOut[]> {
  if (employeesIds.length === 0) {
    return [];
  }
  const { data } = await axios.post<ScheduleOut[]>(`/api/hr/schedules`, {
    siteId: parseInt(siteId),
    relatedSiteIds,
    employeeIds: employeesIds,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  return data;
}

export async function validateSchedules(
  input: ValidateSchedulesIn,
): Promise<boolean> {
  try {
    const resp = await axios.put("/api/hr/schedules/validation", input);
    return resp.status == 200;
  } catch (err) {
    console.error("Failed to validate schedules", err);
    return false;
  }
}

export function deleteSchedules(input: DeleteSchedulesIn): Promise<void> {
  if (input.scheduleIds.length === 0) return Promise.resolve();
  return axios.delete("/api/hr/schedules", { data: input });
}

export function cancelDraftSchedules(
  input: CancelDraftSchedulesIn,
): Promise<void> {
  if (input.scheduleIds.length === 0) return Promise.resolve();
  return axios.put("/api/hr/schedules/cancellation", input);
}

function updateSchedules(schedules: ScheduleIn[]): Promise<void> {
  if (schedules.length === 0) return Promise.resolve();
  return axios.put("/api/hr/schedules/update", schedules);
}

function createSchedules(schedules: ScheduleIn[]): Promise<void> {
  if (schedules.length === 0) return Promise.resolve();
  return axios.post("/api/hr/schedules/create", schedules);
}
